import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Trash2 } from 'lucide-react';
import { generateBOLPDF } from './print/BOLPdfGenerator';
import BolToolbar from '../components/toolbars/BolToolBar';
import ProductSelectionModal from '../components/Modals/ProductSelectionModal';
import QuickAddCompanyModal from '../components/Modals/QuickAddCompanyModal';



import '../index.css';

const BillOfLading = () => {
    const { bolID } = useParams(); // Retrieve bolID from the URL
    const [companies, setCompanies] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedShipper, setSelectedShipper] = useState(null);
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [selectedConsignee, setSelectedConsignee] = useState(null);
    const [bolItems, setBolItems] = useState([]);
    const [shipDate, setShipDate] = useState('');
    const [instructions, setInstructions] = useState('');
    const [taxStatus, setTaxStatus] = useState('PAID'); // Default value
    const [showProductModal, setShowProductModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false); // For editing quantities
    const [isEditMode, setIsEditMode] = useState(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [modalRole, setModalRole] = useState(''); // For company role selection
    const [quantities, setQuantities] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const parsedBolID = bolID ? parseInt(bolID, 10) : null;
    const [showQuickAddModal, setShowQuickAddModal] = useState(false);

    useEffect(() => {
        if (bolID) {
            // Fetch Bill of Lading details
            axios.get(`/api/bol/${bolID}`)
                .then((response) => {
                    const bolData = response.data;
    
                    // Map items to include quantity and other details
                    setBolItems(
                        bolData.items.map(item => ({
                            ...item,
                            quantity: item.bolQuantity || 0, // Use bolQuantity for quantity
                            proWeight: Number(item.proWeight || 0),
                            proPackGal: Number(item.proPackGal || 0),
                        }))
                    );
    
                    // Populate other state values
                    setSelectedShipper({
                        id: bolData.destinationID,
                        companyName: bolData.shipperName,
                        addressLine1: bolData.shipperAddress,
                        city: bolData.shipperCity,
                        state: bolData.shipperState,
                        zipCode: bolData.shipperZip,
                        phone: bolData.shipperPhone,
                    });
                    setSelectedCarrier({
                        id: bolData.carrierID,
                        companyName: bolData.carrierName,
                        addressLine1: bolData.carrierAddress,
                        city: bolData.carrierCity,
                        state: bolData.carrierState,
                        zipCode: bolData.carrierZip,
                        phone: bolData.carrierPhone,
                    });
                    setSelectedConsignee({
                        id: bolData.consigneeID,
                        companyName: bolData.consigneeName,
                        addressLine1: bolData.consigneeAddress,
                        city: bolData.consigneeCity,
                        state: bolData.consigneeState,
                        zipCode: bolData.consigneeZip,
                        phone: bolData.consigneePhone,
                    });
    
                    setShipDate(bolData.bolShipdate.split('T')[0]); // Format date for input field
                    setTaxStatus(bolData.bolTAX);
                    setInstructions(bolData.bolInstructions);
                })
                .catch((error) => console.error('Error fetching Bill of Lading:', error));
        }
    }, [bolID]);
//######## Get companies
    useEffect(() => {
        // Fetch companies from the backend
        axios.get('/api/companies')
            .then((response) => {
                console.log('Fetched Companies:', response.data); // Debug fetched companies
                setCompanies(response.data); // Set the companies state with the response data
            })
            .catch((error) => console.error('Error fetching companies:', error));
    }, []);
//######## Get products
    useEffect(() => {
        axios.get('/api/products')
            .then((response) => {
                if (Array.isArray(response.data.products)) {
                    setProducts(response.data.products); // Access the nested products array
                } else {
                    console.error('Unexpected response format:', response.data);
                    setProducts([]); // Fallback to an empty array if the structure is unexpected
                }
            })
            .catch((error) => console.error('Error fetching products:', error));
    }, []);

    // Calculate totals for weight and gallons
    const calculateTotals = () => {
        const totalWeight = bolItems.reduce((sum, item) => {
            const weight = (Number(item.quantity) || 0) * (Number(item.proWeight) || 0);
            return sum + weight;
        }, 0);
    
        const totalGallons = bolItems.reduce((sum, item) => {
            const gallons = (Number(item.quantity) || 0) * (Number(item.proPackGal) || 0);
            return sum + gallons;
        }, 0);
    
        return {
            totalWeight: totalWeight.toFixed(2),
            totalGallons: totalGallons.toFixed(2),
        };
    };
    
    const bolData = {
        shipperID: selectedShipper?.id || null, // Safely access id or set to null
        carrierID: selectedCarrier?.id || null,
        consigneeID: selectedConsignee?.id || null,
        shipDate,
        instructions,
        bolTAX: taxStatus,
        items: bolItems.map(item => ({
            productID: item.productID,
            quantity: item.quantity || 0,
            vol: item.quantity && item.proPackGal ? (item.quantity * item.proPackGal).toFixed(2) : '0.00',
            weight: item.quantity && item.proWeight ? (item.quantity * item.proWeight).toFixed(2) : '0.00',
        })),
    };

    const handleAddProducts = () => {
        if (isEditMode) {
            // Handle editing existing items
            const updatedItems = bolItems.map(item => ({
                ...item,
                quantity: quantities[item.productID] || item.quantity,
                // Recalculate weight and volume based on new quantity
                weight: ((quantities[item.productID] || item.quantity) * item.proWeight).toFixed(2),
                vol: ((quantities[item.productID] || item.quantity) * item.proPackGal).toFixed(2)
            }));
            setBolItems(updatedItems);
        } else {
            // Handle adding new products (existing logic)
            const selectedProducts = products
                .filter(product => quantities[product.productID] > 0)
                .map(product => ({
                    ...product,
                    quantity: quantities[product.productID],
                    weight: (quantities[product.productID] * product.proWeight).toFixed(2),
                    vol: (quantities[product.productID] * product.proPackGal).toFixed(2)
                }));
            setBolItems(prev => [...prev, ...selectedProducts]);
        }
        setQuantities({});
        setShowProductModal(false);
        setIsEditMode(false);
    };
    
    const handleEditQuantitiesClick = () => {
        // Pre-populate quantities with current values
        const currentQuantities = {};
        bolItems.forEach(item => {
            currentQuantities[item.productID] = item.quantity;
        });
        
        // Debugging log
        console.log('Edit Quantities - Current Items:', bolItems);
        console.log('Edit Quantities - Populated Quantities:', currentQuantities);
        
        setQuantities(currentQuantities);
        setIsEditMode(true);
        setShowProductModal(true);
    };

    const handleSaveCompany = (companyID) => {
        const selectedCompany = companies.find((c) => c.id === companyID);
        if (!selectedCompany) return;
    
        // Find the shipping address from the addresses array
        const shippingAddress = selectedCompany.addresses?.find(
            addr => addr.addressType === 'shipping'
        ) || selectedCompany.addresses?.[0]; // fallback to first address if no shipping address
    
        // Create a formatted company object with address details
        const formattedCompany = {
            id: selectedCompany.id,
            companyName: selectedCompany.companyName,
            phone: selectedCompany.phone,
            addressLine1: shippingAddress?.addressLine || '',
            city: shippingAddress?.city || '',
            state: shippingAddress?.state || '',
            zipCode: shippingAddress?.zip || '',
        };
    
        // Assign the formatted company to the appropriate role
        if (modalRole === 'Shipper') setSelectedShipper(formattedCompany);
        if (modalRole === 'Carrier') setSelectedCarrier(formattedCompany);
        if (modalRole === 'Consignee') setSelectedConsignee(formattedCompany);
    
        setShowCompanyModal(false);
    };

    const openCompanyModal = (role) => {
        setModalRole(role);
        setSearchTerm('');
        setFilteredCompanies([]);
        setShowCompanyModal(true);
    };
    const handleCompanyAdded = async (newCompany) => {
        try {
            // Refresh the companies list
            const response = await axios.get('/api/companies');
            setCompanies(response.data);
            
            // If the modal role is set, automatically select the new company
            if (modalRole) {
                const formattedCompany = {
                    id: newCompany.id,
                    companyName: newCompany.companyName,
                    phone: newCompany.phone,
                    addressLine1: newCompany.addresses?.[0]?.addressLine || '',
                    city: newCompany.addresses?.[0]?.city || '',
                    state: newCompany.addresses?.[0]?.state || '',
                    zipCode: newCompany.addresses?.[0]?.zip || '',
                };
    
                if (modalRole === 'Shipper') setSelectedShipper(formattedCompany);
                if (modalRole === 'Carrier') setSelectedCarrier(formattedCompany);
                if (modalRole === 'Consignee') setSelectedConsignee(formattedCompany);
            }
        } catch (error) {
            console.error('Error refreshing companies:', error);
        }
    };

    const handleSaveBOL = async (callback) => {  // Make sure the async keyword is here
        if (!selectedShipper || !selectedCarrier || !selectedConsignee) {
            alert('Please select Shipper, Carrier, and Consignee.');
            return Promise.reject('Missing required fields');
        }
    
        if (!shipDate) {
            alert('Please select a valid Ship Date.');
            return Promise.reject('Missing ship date');
        }
    
        const totalWeight = bolItems.reduce((sum, item) => {
            return sum + (item.quantity * item.proWeight);
        }, 0);
    
        const bolData = {
            shipperID: selectedShipper?.id || 0,
            carrierID: selectedCarrier?.id || 0,
            consigneeID: selectedConsignee?.id || 0,
            shipDate: shipDate || '',
            instructions: instructions || '',
            bolTAX: taxStatus || 'PAID',
            bolTotalWeight: totalWeight.toFixed(2),
            items: bolItems.map(item => ({
                productID: item.productID,
                quantity: item.quantity || 0,
                vol: ((item.proLiters || 0) * (item.proPack || 1) / 3.785).toFixed(2),
                weight: ((item.proWeight || 0) * (item.quantity || 0)).toFixed(2)
            }))
        };
    
        try {
            let savedBolId = parsedBolID;
            if (!parsedBolID) {
                // If creating new BOL
                const response = await axios.post('/api/bol', bolData);
                // Assuming the API returns the new BOL ID in the response
                savedBolId = response.data.id || response.data.bolID;
                // Update the URL without refreshing the page
                window.history.pushState({}, '', `/bill-of-lading/${savedBolId}`);
            } else {
                // If updating existing BOL
                await axios.put(`/api/bol/${parsedBolID}`, bolData);
            }
            
            alert('Bill of Lading saved successfully!');
            return savedBolId; // Return the BOL ID
        } catch (error) {
            alert('Error saving Bill of Lading. Please try again.');
            console.error('Error saving Bill of Lading:', error);
            return Promise.reject(error);
        }
    };

//########## Navigating back to Bol List after Delete
    const navigate = useNavigate();

    const handleDeleteBOL = () => {
        if (!bolID) {
            alert("Invalid Bill of Lading. Cannot delete.");
            return;
        }
    
        if (window.confirm("Are you sure you want to delete this Bill of Lading? This action cannot be undone.")) {
            axios.delete(`/api/bol/${bolID}`)
                .then(() => {
                    alert("Bill of Lading deleted successfully!");
                    navigate("/bill-of-lading-list"); // Redirect to the list page
                })
                .catch(err => {
                    console.error("Error deleting Bill of Lading:", err);
                    alert("Failed to delete the Bill of Lading. Please try again.");
                });
        }
    };  
//########## Under and over 14, 16% etoh    
    const totalGallonsUnder14 = bolItems
        .filter(item => Number(item.proAlc) <= 14)
        .reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.proPackGal) || 0)), 0)
        .toFixed(2);

    const totalGallonsOver14 = bolItems
        .filter(item => Number(item.proAlc) > 14 && Number(item.proAlc) <= 16)
        .reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.proPackGal) || 0)), 0)
        .toFixed(2);

    const totalGallonsOver16 = bolItems
        .filter(item => Number(item.proAlc) > 16)
        .reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.proPackGal) || 0)), 0)
        .toFixed(2);

        return (
            <>
                <BolToolbar 
                    onSave={handleSaveBOL}
                    onPrint={() => {
                        handleSaveBOL()
                            .then((savedBolId) => {
                                if (!savedBolId) {
                                    throw new Error('No BOL ID available');
                                }
                                return axios.get(`/api/bol/${savedBolId}`)
                                    .then((response) => {
                                        // Format the data before passing to PDF generator
                                        const formattedData = {
                                            ...response.data,
                                            items: response.data.items.map(item => ({
                                                quantity: item.bolQuantity || 0,  // Use bolQuantity instead of quantity
                                                proVintage: item.proVintage || '',
                                                proVarietal: item.proVarietal || '',
                                                proFanciful: item.proFanciful || '',
                                                proPack: item.proPack || 0,
                                                proLiters: item.proLiters || 0,
                                                proAlc: item.proAlc || 0,
                                                proWineType: item.proWineType || '',
                                                proAppellation: item.proAppellation || '',
                                                bolItemGalSum: item.bolItemGalSum || 0,
                                                bolWeightSub: item.bolWeightSub || 0
                                            }))
                                        };
                                        
                                        generateBOLPDF(formattedData);
                                    })
                                    .catch((error) => {
                                        console.error('Error fetching BOL data for PDF:', error);
                                        alert('Failed to generate PDF. Please try again.');
                                    });
                            })
                            .catch((error) => {
                                alert('Failed to save Bill of Lading. Please fix the issues and try again.');
                                console.error('Save BOL error:', error);
                            });
                    }}
                    onAddProducts={() => {
                        setQuantities({});
                        setShowProductModal(true);
                    }}
                    
                    onEditQuantities={handleEditQuantitiesClick}
                    onNavigateToList={() => navigate('/bill-of-lading-list')}
                    onNavigateToCompanies={() => {
                        setModalRole('');
                        setShowQuickAddModal(true);
                    }}
                    bolID={bolID}
                    onDelete={handleDeleteBOL}
                />
    
                <div className="container mt-4">
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label">Tax Status</label>
                            <select
                                className="form-control"
                                value={taxStatus}
                                onChange={(e) => setTaxStatus(e.target.value)}
                            >
                                <option value="PAID">TAX PAID</option>
                                <option value="BOND">IN BOND</option>
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Ship Date</label>
                            <input 
                                type="date" 
                                className="form-control" 
                                value={shipDate} 
                                onChange={(e) => setShipDate(e.target.value)} 
                            />
                        </div>
                    </div>
    
                    {/* Company Cards Section */}
                    <div className="row mb-4">
                        {['Shipper', 'Carrier', 'Consignee'].map((role, idx) => {
                            const selected = [selectedShipper, selectedCarrier, selectedConsignee][idx];
                            return (
                                <div key={role} className="col-md-4">
                                    <div className="card h-100">
                                        <div className="card-header bg-light">
                                            <strong>{role}</strong>
                                        </div>
                                        <div className="card-body">
                                            {selected ? (
                                                <div>
                                                    <h6 className="card-title">{selected.companyName}</h6>
                                                    <p className="card-text">
                                                        {selected.addressLine1}<br />
                                                        {selected.city}, {selected.state} {selected.zipCode}<br />
                                                        {selected.phone}
                                                    </p>
                                                    <button 
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => {
                                                            if (role === 'Shipper') setSelectedShipper(null);
                                                            if (role === 'Carrier') setSelectedCarrier(null);
                                                            if (role === 'Consignee') setSelectedConsignee(null);
                                                        }}
                                                    >
                                                        <UserPlus size={16} className="me-1" />
                                                        Change
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => openCompanyModal(role)}
                                                >
                                                    <UserPlus size={16} className="me-1" />
                                                    Select {role}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
    
                    {/* Products Section */}
                    <h5 className="mb-3">Products</h5>
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Quantity</th>
                                    <th>Description (Contains Sulfites)</th>
                                    <th>Weight (lbs)</th>
                                    <th>Alcohol</th>
                                    <th>Gallons</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bolItems.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.quantity}</td>
                                        <td>
                                        {item.proVintage === 0 || item.proVintage === '0' ? "N/V" : item.proVintage} {item.proBrand} {item.proVarietal} {item.proFanciful}
                                            <br />
                                            <small className="text-muted">
                                                {item.proWineType || ''} | {item.proAppellation || ''}
                                            </small>
                                        </td>
                                        <td>{typeof item.proWeight === 'number' ? item.proWeight.toFixed(2) : '0.00'}</td>
                                        <td>{item.proAlc || 'N/A'}%</td>
                                        <td>{(item.quantity * item.proPackGal).toFixed(2)}</td>
                                        <td className="text-end">
                                            <button 
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => {
                                                    // Remove the item from bolItems
                                                    const updatedItems = bolItems.filter((_, index) => index !== idx);
                                                    setBolItems(updatedItems);
                                                }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
    
                    {/* Totals Section */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-4">
                                    <small className="text-muted">Under 14% Alcohol</small>
                                    <h6>{totalGallonsUnder14} gallons</h6>
                                </div>
                                <div className="col-md-4">
                                    <small className="text-muted">14-16% Alcohol</small>
                                    <h6>{totalGallonsOver14} gallons</h6>
                                </div>
                                <div className="col-md-4">
                                    <small className="text-muted">Over 16% Alcohol</small>
                                    <h6>{totalGallonsOver16} gallons</h6>
                                </div>
                            </div>
                        </div>
                    </div>
    
                    {/* Instructions */}
                    <div className="mb-4">
                        <label className="form-label">Instructions</label>
                        <textarea 
                            className="form-control" 
                            rows="3" 
                            value={instructions} 
                            onChange={(e) => setInstructions(e.target.value)}
                        ></textarea>
                    </div>
            {/* Company Selection Modal */}
            {showCompanyModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Select {modalRole}</h5>
                                    <button
                                        className="btn-close"
                                        onClick={() => setShowCompanyModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <input
                                        type="text"
                                        className="form-control mb-3"
                                        placeholder="Search company..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            const term = e.target.value.toLowerCase();
                                            setSearchTerm(term);
                                            setFilteredCompanies(
                                                companies.filter(company =>
                                                    company.coCompanyName.toLowerCase().includes(term)
                                                )
                                            );
                                        }}
                                    />
                                    <ul className="list-group">
                                        {(filteredCompanies.length ? filteredCompanies : companies).map(company => (
                                            <li
                                                key={company.id}
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                            >
                                                {company.companyName}
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleSaveCompany(company.id)}
                                                >
                                                    Select
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {/* Products Modal */}
            {showProductModal && (
                <ProductSelectionModal
                    show={showProductModal}
                    onClose={() => {
                        setQuantities({});
                        setShowProductModal(false);
                        setIsEditMode(false);
                    }}
                    products={isEditMode ? bolItems : products}
                    quantities={quantities}
                    onQuantityChange={(productID, value) => {
                        setQuantities(prev => ({
                            ...prev,
                            [productID]: value,
                        }));
                    }}
                    onSave={handleAddProducts}
                    title={isEditMode ? "Edit Product Quantities" : "Select Products"}
                />
            )}
            {/* Company Modal */}
            <QuickAddCompanyModal
                show={showQuickAddModal}
                onClose={() => setShowQuickAddModal(false)}
                onCompanyAdded={handleCompanyAdded}
            />
        </div>
        </>
    );
};

export default BillOfLading;